import { useContext } from 'react';
import { UserContext } from '../../contexts';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useFeatureFlag } from '../feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../feature-flags/feature-flags.type';
import { Permissions, Roles } from './auth.type';
import type { RolesData } from './auth.type';

/**
 * Returns the user's role and whether they are an admin based on the user's
 * organization memberships and the organization's portfolio.
 *
 * @returns {RolesData} Roles data object
 */
const useRoles = (): RolesData => {
  const { user } = useContext(UserContext);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const organizationIdGlobal = useGlobalStore((state) => state.organization?.id);
  const organizationIdPortfolio = portfolio?.organization?.id;
  const organizationId = organizationIdGlobal ?? organizationIdPortfolio;

  if (!organizationId) {
    return {
      role: Roles.USER,
      isAdmin: false,
      admins: [],
    };
  }

  const isAdmin = user?.organization_memberships[organizationId]?.is_admin || false;
  const admins = user?.organization_memberships[organizationId]?.admins || [];

  return {
    role: isAdmin ? Roles.ADMIN : Roles.USER,
    isAdmin,
    admins,
  };
};

/**
 * Returns the organization id associated with the user, or null if not found.
 *
 * @returns {string | null} The organization id, or null if not found
 */
const useOrganizationId = (): string | null => {
  const { user } = useContext(UserContext);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const organizationIdGlobal = useGlobalStore((state) => state.organization?.id);
  if (organizationIdGlobal) {
    return organizationIdGlobal;
  }

  // Having this as an if else clause is important in order to avoid
  // extra react query from being fired which causes projects/boards/...... to
  // not show up even after a successful external system authentication
  if (portfolio && user) {
    if (portfolio?.organization?.id) {
      return portfolio?.organization?.id;
    } else if (user?.organizations[0]) {
      return user?.organizations[0];
    }
    return null;
  } else {
    return null;
  }
};

/**
 * Checks if the current user has a specific RBAC permission for the current organization.
 * Returns true (fail-open) when: RBAC is not enabled, no organization context
 * is resolved, or the user's membership has no permissions property (legacy/migration).
 * Returns false when RBAC is enabled and the permission is absent.
 *
 * @param {Permissions} permission - The permission to check (e.g. Permissions.FinancialsRead)
 * @returns {boolean} true if the user has the permission or access is unrestricted
 */
const useHasPermissions = (permission: Permissions): boolean => {
  const { user } = useContext(UserContext);
  const organizationId = useOrganizationId();
  const isRbacEnabled = useFeatureFlag(FeatureFlag.RbacEnabled);

  if (!isRbacEnabled || !organizationId) {
    return true;
  }

  const permissions = user?.organization_memberships[organizationId]?.permissions;
  // undefined = BE hasn't sent permissions yet (legacy/migration), fail-open to avoid breaking users.
  // [] = RBAC enabled, user has no groups, correctly denies access via includes() returning false.
  if (!permissions) {
    return true;
  }

  return permissions.includes(permission);
};

export { useHasPermissions, useOrganizationId, useRoles };
